import { Badge, PageHeader, Skeleton } from '@zyxantech/tux'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetCustomersQuery } from '@/api/partners.api'
import { EmptyState } from '@/components/shared/EmptyState'
import { ErrorState } from '@/components/shared/ErrorState'
import { ROUTES } from '@/constants/routes'
import { formatTimestamp } from '@/utils/format'
import { useMyPartner } from './hooks/useMyPartner'

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { partner, isLoading: partnerLoading } = useMyPartner()

  const { data, isLoading, error, refetch } = useGetCustomersQuery(
    { id: partner?.id ?? '' },
    { skip: !partner },
  )

  const customer = data?.items.find((c) => c.id === id)

  if (partnerLoading || isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="30%" />
        <Skeleton height={300} />
      </div>
    )
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorState error={error} retry={() => void refetch()} />
      </div>
    )
  }
  if (!customer) {
    return (
      <div className="p-6">
        <EmptyState title="Customer not found" message="This tenant may have been removed." />
      </div>
    )
  }

  const modules = customer.moduleConfig

  return (
    <div className="p-6">
      <button
        type="button"
        onClick={() => navigate(ROUTES.CUSTOMERS)}
        className="mb-4 flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary"
      >
        <ArrowLeft size={14} /> Customers
      </button>

      <PageHeader
        title={customer.name}
        subtitle={`Tenant · ${customer.slug}`}
        actions={
          <Badge variant={customer.status === 'active' ? 'success' : 'default'}>
            {customer.status}
          </Badge>
        }
      />

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Details */}
        <section>
          <h2 className="mb-3 text-sm font-semibold text-text-primary">Details</h2>
          <div className="space-y-2 rounded-lg border border-border-subtle bg-bg-surface p-4 text-sm">
            <Row label="ID" value={customer.id} mono />
            <Row label="Partner ID" value={customer.partnerId ?? '—'} mono />
            <Row label="Created" value={formatTimestamp(customer.createdAt)} />
            <Row label="Updated" value={formatTimestamp(customer.updatedAt)} />
          </div>
        </section>

        {/* Module config */}
        <section>
          <h2 className="mb-3 text-sm font-semibold text-text-primary">Module config</h2>
          <div className="rounded-lg border border-border-subtle bg-bg-surface p-4">
            {modules && Object.keys(modules).length > 0 ? (
              <div className="space-y-2 text-sm">
                {Object.entries(modules).map(([k, v]) => (
                  <Row key={k} label={k} value={JSON.stringify(v)} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted">No module configuration.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-text-muted">{label}</span>
      <span className={mono ? 'truncate font-mono text-xs text-text-secondary' : 'text-text-secondary'}>
        {value}
      </span>
    </div>
  )
}
